import { useCallback, useEffect, useState } from "react"

/**
 * Copies text to the clipboard
 * ref: https://www.30secondsofcode.org/react/s/use-copy-to-clipboard
 * @param text - text to copy
 * @param timeout - timeout in ms
 * @example const [copied, copyToClipboard] = useCopyToClipboard('some text', 5000);
 */
export const useCopyToClipboard = (
  text: string,
  timeout = 3000
): [boolean, () => void] => {
  const [copied, setCopied] = useState(false)
  useEffect(() => () => setCopied(false), [text])

  const copyToClipboard = async (text: string) => {
    const clipboard = navigator.clipboard

    if (!clipboard) {
      // Fallback to document.execCommand()
      const el = document.createElement("textarea")
      el.value = text
      document.body.appendChild(el)
      el.select()
      const success = document.execCommand("copy")
      document.body.removeChild(el)
      return success
    } else {
      return await navigator.clipboard.writeText(text)
    }
  }

  const copy = useCallback(() => {
    if (!copied) {
      copyToClipboard(text)
        .then(() => setCopied(true))
        .catch(() => setCopied(false))
    }

    // Reset after 3 seconds
    setTimeout(() => {
      setCopied(false)
    }, timeout)
  }, [text])

  return [copied, copy]
}

export default useCopyToClipboard
